import React from 'react'
import {
    Box,
    Button,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline'
import SettingsIcon from '@material-ui/icons/Settings'
import { Link } from 'react-router-dom'

import { Campaign, CampaignSystemKind } from '../../../../models/Outreach/types'
import { seasonCampaignDetailPath } from '../../Campaigns/utils/seasonCampaignUtils'
import {
    getPrimaryCampaignDisplayStatus,
    PRIMARY_CAMPAIGN_STATUS_LABELS,
} from '../../utils/campaignDisplayStatus'

const useStyles = makeStyles((theme: Theme) => ({
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    sectionHeader: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.25),
    },
    sectionTitle: {
        fontSize: '1rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    sectionHint: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },
    tableContainer: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        boxShadow: 'none',
    },
    tableHead: {
        backgroundColor: theme.palette.grey[50],
        '& .MuiTableCell-head': {
            fontWeight: 600,
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
        },
    },
    nameCell: {
        minWidth: 180,
    },
    campaignName: {
        fontWeight: 600,
        fontSize: '0.875rem',
    },
    campaignType: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    numeric: {
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
    },
    statusChip: {
        height: 22,
        fontSize: '0.65rem',
        fontWeight: 600,
    },
    statusActive: {
        backgroundColor: '#ecfdf5',
        color: '#047857',
    },
    statusReady: {
        backgroundColor: '#eff6ff',
        color: '#1d4ed8',
    },
    statusSetup: {
        backgroundColor: '#fef3c7',
        color: '#b45309',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.5),
        whiteSpace: 'nowrap',
    },
    empty: {
        padding: theme.spacing(4),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
}))

function campaignTypeLabel(campaign: Campaign): string {
    if (campaign.system_kind === CampaignSystemKind.PRIMARY_EMAIL) {
        return 'Primary email'
    }
    if (campaign.system_kind === CampaignSystemKind.PRIMARY_TEXT) {
        return 'Primary text'
    }
    return 'Custom'
}

function displayName(campaign: Campaign): string {
    if (campaign.system_kind === CampaignSystemKind.PRIMARY_EMAIL) {
        return 'Primary Email Outreach'
    }
    if (campaign.system_kind === CampaignSystemKind.PRIMARY_TEXT) {
        return 'Primary Text Outreach'
    }
    return campaign.name
}

interface CampaignPerformanceTableProps {
    campaigns: Campaign[]
    seasonId?: number | null
}

export const CampaignPerformanceTable: React.FC<CampaignPerformanceTableProps> = ({
    campaigns,
    seasonId,
}) => {
    const classes = useStyles()

    return (
        <Box className={classes.section}>
            <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                    Campaign performance
                </Typography>
                <Typography className={classes.sectionHint}>
                    Season-wide outreach at a glance. Open tracking to see who
                    is enrolled in each campaign.
                </Typography>
            </Box>

            {campaigns.length === 0 ? (
                <Paper className={classes.tableContainer} variant="outlined">
                    <Typography className={classes.empty}>
                        No campaigns for this season yet. Create a primary or
                        custom campaign below to start tracking performance.
                    </Typography>
                </Paper>
            ) : (
                <TableContainer
                    component={Paper}
                    className={classes.tableContainer}
                >
                    <Table size="small" aria-label="campaign performance">
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell>Campaign</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Enrolled</TableCell>
                                <TableCell align="right">Pending</TableCell>
                                <TableCell align="right">Sent</TableCell>
                                <TableCell align="right">Opens</TableCell>
                                <TableCell align="right">Clicks</TableCell>
                                <TableCell align="right">Converted</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {campaigns.map((campaign) => {
                                const displayStatus = getPrimaryCampaignDisplayStatus(
                                    campaign,
                                )
                                const statusClass =
                                    displayStatus === 'active'
                                        ? classes.statusActive
                                        : displayStatus === 'ready_paused'
                                        ? classes.statusReady
                                        : classes.statusSetup
                                const configureHref = seasonCampaignDetailPath(
                                    campaign.id,
                                    seasonId ?? undefined,
                                )
                                const trackingHref = seasonCampaignDetailPath(
                                    campaign.id,
                                    seasonId ?? undefined,
                                    'tracking',
                                )

                                return (
                                    <TableRow key={campaign.id} hover>
                                        <TableCell className={classes.nameCell}>
                                            <Typography
                                                className={classes.campaignName}
                                            >
                                                {displayName(campaign)}
                                            </Typography>
                                            <Typography
                                                className={classes.campaignType}
                                            >
                                                {campaignTypeLabel(campaign)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    PRIMARY_CAMPAIGN_STATUS_LABELS[
                                                        displayStatus
                                                    ]
                                                }
                                                size="small"
                                                className={`${classes.statusChip} ${statusClass}`}
                                            />
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            className={classes.numeric}
                                        >
                                            {(
                                                campaign.total_prospects ?? 0
                                            ).toLocaleString()}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            className={classes.numeric}
                                        >
                                            {(
                                                campaign.pending_prospects ?? 0
                                            ).toLocaleString()}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            className={classes.numeric}
                                        >
                                            {(
                                                campaign.total_sent ?? 0
                                            ).toLocaleString()}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            className={classes.numeric}
                                        >
                                            {(
                                                campaign.total_opens ?? 0
                                            ).toLocaleString()}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            className={classes.numeric}
                                        >
                                            {(
                                                campaign.total_clicks ?? 0
                                            ).toLocaleString()}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            className={classes.numeric}
                                        >
                                            {(
                                                campaign.total_converted ?? 0
                                            ).toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box className={classes.actions}>
                                                <Button
                                                    component={Link}
                                                    to={trackingHref}
                                                    size="small"
                                                    color="primary"
                                                    startIcon={
                                                        <PeopleOutlineIcon fontSize="small" />
                                                    }
                                                >
                                                    Tracking
                                                </Button>
                                                <Button
                                                    component={Link}
                                                    to={configureHref}
                                                    size="small"
                                                    color="primary"
                                                    startIcon={
                                                        <SettingsIcon fontSize="small" />
                                                    }
                                                >
                                                    Configure
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    )
}
